import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Button, Input, Heading, Flex, Text, useToast, Spinner,
  Card, CardBody, VStack, Badge, HStack, Container, 
  useColorModeValue
} from '@chakra-ui/react';
import { EditIcon } from '@chakra-ui/icons';
import { FiRefreshCw } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

const ManageUseConditions = () => {
  // Theme colors - matching AppThemeColors.js
  const themeColor = "#4bedffff";
  const themeTextColor = "black";
  const bgCard = "#fff8e1";

  const [conditions, setConditions] = useState({ title: '', content: '' });
  const [reload, setReload] = useState(false);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  // Fetch the current Conditions on component mount or reload
  const hasFetched = useRef(false);
  const prevReload = useRef(reload);
  useEffect(() => {
    if (hasFetched.current && prevReload.current === reload) return;
    hasFetched.current = true;
    prevReload.current = reload;
    fetchConditions();
  }, [reload]);

  // Fetch Conditions data from the API
  const fetchConditions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/conditions`);
      const data = await response.json();
      if (data) {
        setConditions(data);
      }
    } catch (error) {
      console.error('Failed to fetch conditions:', error);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar condições de uso',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConditions({ ...conditions, [name]: value });
  };

  // Handle content change in the ReactQuill editor
  const handleContentChange = (content) => {
    setConditions({ ...conditions, content });
  };

  // Update the Conditions with a PUT request
  const handleUpdateConditions = async () => {
    setLoading(true);
    const token = localStorage.getItem('userToken_TOVIAPPS');
    try {
      const response = await fetch(`${API_BASE_URL}api/conditions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(conditions),
      });

      if (!response.ok) {
        throw new Error('Falha ao atualizar condições de uso');
      }

      setReload(!reload);
      toast({
        title: "Sucesso!",
        description: "Condições de Uso foram atualizadas com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error('Error updating conditions:', error);
      toast({
        title: 'Erro!',
        description: 'Falha ao atualizar condições de uso. Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  // Define extended toolbar options for ReactQuill editor
  const modules = {
    toolbar: [
      [{ 'header': '1' }, { 'header': '2' }, { 'header': [3, 4, 5, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean'],
      [{ 'direction': 'rtl' }]
    ],
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'link', 'image', 'font', 'size',
    'color', 'background', 'align', 'indent', 'blockquote',
    'code-block', 'direction'
  ];

  if (loading) {
    return (
      <Container maxW="container.xl" p={6}>
        <Card shadow="md" borderRadius="lg" overflow="hidden" bg={bgCard}>
          <CardBody>
            <Flex justify="center" align="center" h="200px">
              <Spinner size="xl" thickness="4px" color={themeColor} />
              <Text ml={4} fontWeight="medium" color="black">Carregando condições de uso...</Text>
            </Flex>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" p={6}>
      {/* Header Card */}
      <Card mb={6} shadow="md" borderRadius="lg" overflow="hidden" bg={bgCard}>
        <CardBody>
          <Flex justify="space-between" align="center" wrap="wrap">
            <HStack>
              <Heading as="h1" fontSize="2xl" fontWeight="bold" color="black">
                Gestão dos Termos de Condição de Uso
              </Heading>
              <Badge bg={themeColor} color={themeTextColor} borderRadius="full" px={3} py={1}>
                Documento
              </Badge>
            </HStack>
            
            <HStack spacing={3}>
              <Button
                leftIcon={<FiRefreshCw />}
                size="sm"
                variant="outline"
                borderColor={themeColor}
                color="black"
                onClick={fetchConditions}
                isLoading={loading}
              >
                Atualizar
              </Button>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Editor Card */}
      <Card shadow="md" borderRadius="lg" overflow="hidden" mb={6} bg={bgCard}>
        <Box bg={themeColor} h="8px" w="100%" />
        <CardBody p={4}>
          <VStack spacing={4} align="stretch">
            <Text fontWeight="bold" color="black">Editor de Condições de Uso</Text>
            
            <Input
              placeholder="Título"
              name="title"
              value={conditions.title}
              onChange={handleInputChange}
              bg="white"
              color="black"
              focusBorderColor={themeColor}
              size="md"
            />
            
            <Box 
              borderRadius="md" 
              overflow="hidden" 
              border="1px solid" 
              borderColor="gray.200"
            >
              <ReactQuill
                theme="snow"
                value={conditions.content}
                onChange={handleContentChange}
                modules={modules}
                formats={formats}
                style={{ 
                  height: '500px',
                  backgroundColor: 'white'
                }}
              />
            </Box>
            
            <Button
              bg={themeColor}
              color={themeTextColor}
              onClick={handleUpdateConditions}
              isLoading={loading}
              size="md"
              width="100%"
              mt={2}
              _hover={{ bg: "#e5c100" }}
              leftIcon={<EditIcon />}
            >
              Atualizar Condições de Uso
            </Button>
          </VStack>
        </CardBody>
      </Card>

      {/* Preview Card */}
      <Card shadow="md" borderRadius="lg" overflow="hidden" bg={bgCard}>
        <Box bg={themeColor} h="8px" w="100%" />
        <CardBody p={4}>
          <VStack spacing={4} align="stretch">
            <Heading size="md" color="black">Como o Celular Mostra o Documento</Heading>
            
            <Card bg="white" shadow="md" borderRadius="lg">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="black">{conditions.title}</Heading>
                  <Box
                    dangerouslySetInnerHTML={{ __html: conditions.content }}
                    maxH="600px"
                    overflowY="auto"
                    p={4}
                    borderRadius="md"
                    bg="white"
                    color="black"
                  />
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
};

export default ManageUseConditions;